import React, { useContext, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { CardTypes } from 'ts-interfaces';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ShareIcon from '@mui/icons-material/Share';
import { useProjectSchemas } from '../../global-state/useProjectSchemas';
import { AuthContext } from '../../auth/Auth';


export const ExistingCardMoreOptions = ({ 
  type, 
  onEdit, 
  onDelete, 
  onCopy, 
  onRename, 
  onAssociatedProjectChange, 
  onShare, 
  onRemoveFromProject, 
  cardSize, 
  disabled = false, 
  isTeamsProUser = false, 
  isProUser = false 
}: {
  type: any;
  onEdit: () => void;
  onDelete: (event: React.MouseEvent) => void;
  onCopy: (event: React.MouseEvent) => void;
  onRename: (event: React.MouseEvent) => void;
  onAssociatedProjectChange: (event: React.MouseEvent) => void;
  onShare: (event: React.MouseEvent) => void;
  onRemoveFromProject: () => void;
  cardSize: string;
  disabled?: boolean;
  isTeamsProUser?: boolean;
  isProUser?: boolean;
}) => {
  // console.log('ExistingCardMoreOptions render');
  // const {currentUser} = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  // Get the loading state and projects data from the store
  const { ownedProjects, sharedProjects, projectsLoaded, isProjectsLoading } = useProjectSchemas((state) => ({ 
    ownedProjects: state.ownedProjects, 
    sharedProjects: state.sharedProjects,
    projectsLoaded: state.projectsLoaded,
    isProjectsLoading: state.isProjectsLoading
  }));

  // Check if we have projects available
  // We'll show the menu item if:
  // 1. Projects are loaded AND we have projects, OR
  // 2. Projects are not loaded yet (we don't know if there are projects, so show the option)
  const hasProjectsAvailable = !projectsLoaded || (projectsLoaded && (ownedProjects.length > 0 || sharedProjects.length > 0));
  
  

  const open = Boolean(anchorEl);
  const buttonRef = useRef(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Always allow menu to open, even when disabled - we'll handle item-level disabling
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    // Handle both direct calls and event-based calls
    if (event) {
      event.stopPropagation();
    }
    
    // Store button ref so you can focus it after closing
    const currentButton = anchorEl;
    
    // Close the menu first
    setAnchorEl(null);
    
    // Then focus the button after a short delay
    setTimeout(() => {
      if (currentButton) {
        currentButton.focus();
      }
    }, 10);
  };

  const handleMenuItemClick = (handler: ((event: React.MouseEvent) => void) | (() => void)) => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (handler) {
      handler(event);
    }
    handleClose(event);
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        aria-label="more options"
        data-testid="collection-more-options"
        aria-controls={open ? 'project-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
        sx={{ 
          padding: 0,
          color: 'inherit',
          zIndex: 2, // Ensure button is above other elements
          position: 'relative'
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      
      <Menu
        id="project-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        MenuListProps={{
          'aria-labelledby': 'project-options-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {type === CardTypes.Projects && 
          <MenuItem onClick={handleMenuItemClick(onEdit)} disabled={disabled}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Project Details</ListItemText>
          </MenuItem>
        }

        {type === CardTypes.Collections && 
          <MenuItem onClick={handleMenuItemClick(onEdit)} disabled={disabled}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit This Collection</ListItemText>
          </MenuItem>
        }

        {type === CardTypes.Boards &&
            <MenuItem onClick={handleMenuItemClick(onRename)} disabled={disabled}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
        }
        
        {(type === CardTypes.Boards || type === CardTypes.Collections) && hasProjectsAvailable && (isProUser || isTeamsProUser) &&
          <MenuItem onClick={handleMenuItemClick(onAssociatedProjectChange)} disabled={disabled}>
            <ListItemIcon>
              <DriveFileMoveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Associated Project</ListItemText>
          </MenuItem>
        }
        
        <MenuItem onClick={handleMenuItemClick(onCopy)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
            <ListItemText>Make My Own Copy</ListItemText>
        
        </MenuItem>
        
        <MenuItem onClick={handleMenuItemClick(onDelete)} disabled={disabled}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>

        {(type === CardTypes.Boards || type === CardTypes.Collections) && !isTeamsProUser &&
          <MenuItem onClick={handleMenuItemClick(onShare)} disabled={disabled}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share Copy</ListItemText>
          </MenuItem>
        }
        
        
        {/* {(type === CardTypes.Boards || type === CardTypes.Collections) && cardSize === 'small' && (isProUser || isTeamsProUser) &&
          <MenuItem onClick={handleMenuItemClick(onRemoveFromProject)} disabled={disabled}>
            <ListItemIcon>
              <CancelPresentationIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove From Project</ListItemText>
          </MenuItem>
        } */}

        {/* <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <StarIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Star</ListItemText>
        </MenuItem> */}
      </Menu>
    </>
  );
};